import { Component, computed, input, output } from '@angular/core';
import { SystemMonitorStat } from 'src/api/system-monitor-stat';

interface SparkSeries {
    key: 'cpu' | 'memory';
    label: string;
    line: string;
    area: string;
    current: number;
    peak: number;
}

const WIDTH = 200;
const HEIGHT = 48;

/**
 * Compact CPU / memory trend for an info tile. Plain SVG rather than Chart.js: it is
 * small enough not to need axes or tooltips, and it follows the theme through CSS.
 * The full chart opens via (expand).
 */
@Component({
    selector: 'app-system-monitor-spark',
    standalone: true,
    template: `
        @if (series().length) {
            <svg class="spark" [attr.viewBox]="'0 0 ' + width + ' ' + height" preserveAspectRatio="none" role="img" [attr.aria-label]="ariaLabel()">
                @for (s of series(); track s.key) {
                    <path class="spark-area" [class]="'spark-area spark-' + s.key" [attr.d]="s.area" />
                    <path class="spark-line" [class]="'spark-line spark-' + s.key" [attr.d]="s.line" vector-effect="non-scaling-stroke" />
                }
            </svg>
            <dl class="spark-legend">
                @for (s of series(); track s.key) {
                    <div class="spark-stat" [class]="'spark-stat spark-' + s.key">
                        <dt><span class="spark-dot"></span>{{ s.label }}</dt>
                        <dd>
                            {{ s.current }}%<small>peak {{ s.peak }}%</small>
                        </dd>
                    </div>
                }
            </dl>
        } @else {
            <p class="spark-empty">{{ loading() ? 'Loading…' : 'No stats in this window yet.' }}</p>
        }
        <button type="button" class="spark-expand" (click)="expand.emit()" [attr.aria-expanded]="expanded()">
            {{ expanded() ? 'Hide full chart' : 'Open full chart' }}
            <i class="pi" [class.pi-chevron-up]="expanded()" [class.pi-chevron-down]="!expanded()" aria-hidden="true"></i>
        </button>
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                min-width: 0;
                --spark-cpu: var(--p-primary-400, var(--primary-color));
                --spark-memory: var(--p-orange-400, #fb923c);
            }
            :host-context(:root:not(.app-dark)) {
                --spark-cpu: var(--p-primary-700, var(--primary-color));
                --spark-memory: var(--p-orange-600, #ea580c);
            }
            .spark {
                display: block;
                width: 100%;
                height: 3rem;
                overflow: visible;
            }
            .spark-line {
                fill: none;
                stroke-width: 1.75;
                stroke-linejoin: round;
                stroke-linecap: round;
            }
            .spark-area {
                stroke: none;
                opacity: 0.12;
            }
            .spark-line.spark-cpu {
                stroke: var(--spark-cpu);
            }
            .spark-line.spark-memory {
                stroke: var(--spark-memory);
            }
            .spark-area.spark-cpu {
                fill: var(--spark-cpu);
            }
            .spark-area.spark-memory {
                fill: var(--spark-memory);
            }
            .spark-legend {
                display: flex;
                flex-wrap: wrap;
                gap: 0.25rem 1rem;
                margin: 0;
            }
            .spark-stat {
                display: flex;
                align-items: baseline;
                gap: 0.4rem;
                font-size: 0.8125rem;
            }
            dt {
                display: flex;
                align-items: center;
                gap: 0.35rem;
                color: var(--text-color-secondary);
            }
            dd {
                margin: 0;
                font-weight: 600;
            }
            small {
                margin-left: 0.35rem;
                font-weight: 400;
                color: var(--text-color-secondary);
            }
            .spark-dot {
                width: 0.5rem;
                height: 0.5rem;
                border-radius: 50%;
            }
            .spark-cpu .spark-dot {
                background: var(--spark-cpu);
            }
            .spark-memory .spark-dot {
                background: var(--spark-memory);
            }
            .spark-empty {
                margin: 0;
                font-size: 0.875rem;
                color: var(--text-color-secondary);
            }
            .spark-expand {
                align-self: flex-start;
                display: inline-flex;
                align-items: center;
                gap: 0.35rem;
                padding: 0;
                border: 0;
                background: none;
                font: inherit;
                font-size: 0.8125rem;
                color: var(--primary-color);
                cursor: pointer;

                &:hover {
                    text-decoration: underline;
                }
                i {
                    font-size: 0.7rem;
                }
            }
        `
    ]
})
export class SystemMonitorSparkComponent {
    readonly width = WIDTH;
    readonly height = HEIGHT;

    stats = input<SystemMonitorStat[]>([]);
    loading = input(false);
    expanded = input(false);
    expand = output<void>();

    readonly series = computed<SparkSeries[]>(() => {
        const points = [...this.stats()].sort((a, b) => a.timestampUtc.localeCompare(b.timestampUtc));
        if (!points.length) return [];
        const cpu = points.map((p) => clampPercent(p.cpu));
        const memory = points.map((p) => (p.memoryTotal ? clampPercent((p.memoryUsed / p.memoryTotal) * 100) : 0));
        return [toSeries('cpu', 'CPU', cpu), toSeries('memory', 'Memory', memory)];
    });

    readonly ariaLabel = computed(() =>
        this.series()
            .map((s) => `${s.label} ${s.current}%, peak ${s.peak}%`)
            .join('; ')
    );
}

function clampPercent(value: number): number {
    return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : 0;
}

function toSeries(key: SparkSeries['key'], label: string, values: number[]): SparkSeries {
    // A single sample still draws a flat line rather than a dot.
    const samples = values.length === 1 ? [values[0], values[0]] : values;
    const step = WIDTH / (samples.length - 1);
    const coords = samples.map((v, i) => `${(i * step).toFixed(1)},${(HEIGHT - (v / 100) * HEIGHT).toFixed(1)}`);
    const line = `M${coords.join(' L')}`;
    return {
        key,
        label,
        line,
        area: `${line} L${WIDTH},${HEIGHT} L0,${HEIGHT} Z`,
        current: values[values.length - 1],
        peak: Math.max(...values)
    };
}
